import React from "react";

interface HeroBannerProps {
  name: string;
  avatarUrl?: string;
  statusText?: string;
}

const getAvatarText = (name: string) => {
  const s = (name || "").trim();
  if (!s) return "US";
  const lastWord = s.split(/\s+/).filter(Boolean).at(-1) ?? "";
  const cleaned = lastWord
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "");
  return (cleaned.slice(0, 2) || "US").toUpperCase();
};

// SVG icons inline
const IconClock = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
    <circle cx="12" cy="12" r="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconPulse = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M22 12h-4l-3 7-6-14-3 7H2" />
  </svg>
);
const IconCalendar = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
    <path
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 2v3M16 2v3M3 9h18M5 5h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
    />
  </svg>
);
const IconShield = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
    <path
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 2l8 4v6c0 5-3.5 9.5-8 10-4.5-.5-8-5-8-10V6l8-4z"
    />
  </svg>
);

export const HeroBanner: React.FC<HeroBannerProps> = ({
  name,
  avatarUrl,
  statusText = "System normal",
}) => {
  const [now, setNow] = React.useState<Date>(() => new Date());
  const [imgOk, setImgOk] = React.useState(true);

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  React.useEffect(() => setImgOk(true), [avatarUrl]);

  const hour = now.getHours();
  const greeting =
    hour < 11
      ? "Good morning"
      : hour < 16
        ? "Good afternoon"
        : hour < 19
          ? "Good evening"
          : "Good night";

  // ✅ Icon giống file zip: dùng emoji theo thời gian
  const greetingIcon =
    hour < 11 ? "🌅" : hour < 16 ? "☀️" : hour < 19 ? "🌇" : "🌙";

  const timeText = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Ho_Chi_Minh",
  }).format(now);

  const dateText = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Ho_Chi_Minh",
  }).format(now);

  const avatarText = React.useMemo(() => getAvatarText(name), [name]);

  return (
    <section className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {/* Nền rất nhẹ, gần như phẳng */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 22% 18%, rgba(16,185,129,0.08), transparent 48%)",
        }}
      />

      <div className="relative z-10 p-6 sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          {/* Left */}
          <div className="flex items-start gap-5 min-w-0">
            <div className="relative shrink-0">
              {/* Avatar: bỏ bóng đổ màu */}
              <div className="h-16 w-16 overflow-hidden rounded-2xl border border-slate-200 bg-emerald-600 sm:h-20 sm:w-20">
                {avatarUrl && imgOk ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="h-full w-full object-cover"
                    onError={() => setImgOk(false)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="text-lg font-black tracking-wider text-white sm:text-xl">
                      {avatarText}
                    </span>
                  </div>
                )}
              </div>

              {/* chấm xanh */}
              <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-emerald-500" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {/* ✅ giống file zip: icon đứng trước chữ */}
                <span className="text-[15px] leading-none select-none">
                  {greetingIcon}
                </span>

                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {greeting}
                </p>
              </div>

              <h2 className="mt-1 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
                {name}
              </h2>

              <p className="mt-1 text-sm font-medium text-slate-600 sm:text-base">
                Welcome to{" "}
                <span className="font-semibold text-emerald-700">
                  Sang Pharma Portal
                </span>
              </p>
            </div>
          </div>

          {/* Right cards: bỏ shadow, chỉ border nhẹ */}
          <div className="flex flex-wrap items-start gap-3 lg:pt-1">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <IconClock className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  System time
                </div>
                <div className="text-sm font-black tabular-nums text-slate-900" suppressHydrationWarning>
                  {timeText}
                </div>
              </div>
            </div>

            {/*
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <IconPulse className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <div className="text-sm font-black text-slate-900">{statusText}</div>
                </div>
              </div>
            </div>
            */}
          </div>
        </div>

        {/* Divider */}
        <div className="my-5 h-px w-full bg-slate-200" />

        {/* Bottom row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 text-slate-600">
            <IconCalendar className="h-4 w-4" />
            <span className="text-sm font-semibold" suppressHydrationWarning>
              {dateText}
            </span>
          </div>

          {/* pill phẳng */}
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-emerald-50 px-4 py-2 text-emerald-700">
            <IconShield className="h-4 w-4" />
            <span className="text-xs font-bold">Secured Connection</span>
          </div>
        </div>
      </div>
    </section>
  );
};
